'use client';

import { useState } from 'react';
import {
  Plus,
  Search,
  Building2,
  GraduationCap,
  Briefcase,
  Users,
  Church,
  Shield,
  MoreVertical,
  Edit3,
  Trash2,
  X
} from 'lucide-react';
import { mockInstitutions } from '@/lib/mock-data';
import { Institution } from '@/lib/types';
import { cn } from '@/lib/utils';

export default function AdminInstitutionsPage() {
  const [institutions, setInstitutions] = useState(mockInstitutions);
  const [searchQuery, setSearchQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState<Institution['type'] | 'all'>('all');
  const [openMenu, setOpenMenu] = useState<string | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);

  const [newInstitution, setNewInstitution] = useState({
    name: '',
    type: 'school' as Institution['type'],
    city: '',
  });

  const typeIcons: Record<Institution['type'], React.ReactNode> = {
    school: <GraduationCap className="w-5 h-5" />,
    university: <GraduationCap className="w-5 h-5" />,
    company: <Briefcase className="w-5 h-5" />,
    military: <Shield className="w-5 h-5" />,
    club: <Users className="w-5 h-5" />,
    church: <Church className="w-5 h-5" />,
    other: <Building2 className="w-5 h-5" />,
  };

  const typeLabels: Record<Institution['type'], string> = {
    school: 'Schule',
    university: 'Universität',
    company: 'Unternehmen',
    military: 'Militär',
    club: 'Verein',
    church: 'Kirche',
    other: 'Sonstige',
  };

  const typeColors: Record<Institution['type'], string> = {
    school: 'bg-blue-100 text-blue-700',
    university: 'bg-indigo-100 text-indigo-700',
    company: 'bg-amber-100 text-amber-700',
    military: 'bg-green-100 text-green-700',
    club: 'bg-rose-100 text-rose-700',
    church: 'bg-purple-100 text-purple-700',
    other: 'bg-gray-100 text-gray-700',
  };

  const filteredInstitutions = institutions.filter(inst => {
    const matchesSearch = !searchQuery ||
      inst.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      inst.city?.toLowerCase().includes(searchQuery.toLowerCase());

    const matchesType = typeFilter === 'all' || inst.type === typeFilter;

    return matchesSearch && matchesType;
  });

  const handleAddInstitution = () => {
    if (!newInstitution.name || !newInstitution.city) return;

    const newInst: Institution = {
      id: `inst-${Date.now()}`,
      name: newInstitution.name,
      type: newInstitution.type,
      city: newInstitution.city,
      region: null,
      country: 'Deutschland',
      memorialCount: 0,
    };

    setInstitutions(prev => [...prev, newInst]);
    setNewInstitution({ name: '', type: 'school', city: '' });
    setShowAddModal(false);
  };

  const handleDelete = (id: string) => {
    if (confirm('Möchten Sie diese Institution wirklich löschen?')) {
      setInstitutions(prev => prev.filter(inst => inst.id !== id));
    }
  };

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-serif text-gray-900">Institutionen</h1>
          <p className="text-gray-500 mt-1">{institutions.length} Institutionen verwalten</p>
        </div>
        <button
          onClick={() => setShowAddModal(true)}
          className="inline-flex items-center gap-2 px-4 py-2.5 bg-memorial-primary text-white rounded-lg hover:bg-memorial-primary/90 transition-colors"
        >
          <Plus className="w-4 h-4" />
          Institution hinzufügen
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-gray-200 p-4">
        <div className="flex flex-col sm:flex-row gap-4">
          {/* Search */}
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Name oder Stadt suchen..."
              className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-memorial-primary/20 focus:border-memorial-primary"
            />
          </div>

          {/* Type filter */}
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value as typeof typeFilter)}
            className="px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-memorial-primary/20 focus:border-memorial-primary bg-white"
          >
            <option value="all">Alle Typen</option>
            {Object.entries(typeLabels).map(([key, label]) => (
              <option key={key} value={key}>{label}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Institutions grid */}
      {filteredInstitutions.length > 0 ? (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredInstitutions.map((institution) => (
            <div
              key={institution.id}
              className="bg-white rounded-xl border border-gray-200 p-5 hover:shadow-md transition-shadow"
            >
              <div className="flex items-start justify-between mb-3">
                <div className={cn(
                  'w-10 h-10 rounded-lg flex items-center justify-center',
                  typeColors[institution.type]
                )}>
                  {typeIcons[institution.type]}
                </div>
                <div className="relative">
                  <button
                    onClick={() => setOpenMenu(openMenu === institution.id ? null : institution.id)}
                    className="p-1 text-gray-400 hover:text-gray-600 rounded"
                  >
                    <MoreVertical className="w-5 h-5" />
                  </button>

                  {openMenu === institution.id && (
                    <>
                      <div
                        className="fixed inset-0 z-10"
                        onClick={() => setOpenMenu(null)}
                      />
                      <div className="absolute right-0 mt-1 w-40 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-20">
                        <button className="flex items-center gap-2 w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                          <Edit3 className="w-4 h-4" />
                          Bearbeiten
                        </button>
                        <button
                          onClick={() => handleDelete(institution.id)}
                          className="flex items-center gap-2 w-full px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                        >
                          <Trash2 className="w-4 h-4" />
                          Löschen
                        </button>
                      </div>
                    </>
                  )}
                </div>
              </div>

              <h3 className="font-medium text-gray-900 mb-1">{institution.name}</h3>
              <p className="text-sm text-gray-500 mb-3">{institution.city}</p>

              <div className="flex items-center justify-between">
                <span className={cn(
                  'px-2.5 py-1 rounded-full text-xs font-medium',
                  typeColors[institution.type]
                )}>
                  {typeLabels[institution.type]}
                </span>
                <span className="text-sm text-gray-500">
                  {institution.memorialCount} Gedenkseiten
                </span>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
          <Building2 className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <h3 className="text-lg font-medium text-gray-900 mb-1">Keine Institutionen gefunden</h3>
          <p className="text-gray-500 mb-4">
            {searchQuery || typeFilter !== 'all'
              ? 'Passen Sie Ihre Suche an oder fügen Sie eine neue Institution hinzu.'
              : 'Fügen Sie Ihre erste Institution hinzu.'}
          </p>
          <button
            onClick={() => setShowAddModal(true)}
            className="inline-flex items-center gap-2 px-4 py-2 bg-memorial-primary text-white rounded-lg hover:bg-memorial-primary/90 transition-colors"
          >
            <Plus className="w-4 h-4" />
            Institution hinzufügen
          </button>
        </div>
      )}

      {/* Add modal */}
      {showAddModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="bg-white rounded-2xl w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-serif text-gray-900">Institution hinzufügen</h2>
              <button
                onClick={() => setShowAddModal(false)}
                className="p-2 text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                <input
                  type="text"
                  value={newInstitution.name}
                  onChange={(e) => setNewInstitution(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="z.B. Gymnasium am Stadtpark"
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-memorial-primary/20 focus:border-memorial-primary"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Typ</label>
                <select
                  value={newInstitution.type}
                  onChange={(e) => setNewInstitution(prev => ({ ...prev, type: e.target.value as Institution['type'] }))}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-memorial-primary/20 focus:border-memorial-primary"
                >
                  {Object.entries(typeLabels).map(([key, label]) => (
                    <option key={key} value={key}>{label}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Stadt</label>
                <input
                  type="text"
                  value={newInstitution.city}
                  onChange={(e) => setNewInstitution(prev => ({ ...prev, city: e.target.value }))}
                  placeholder="z.B. Düsseldorf"
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-memorial-primary/20 focus:border-memorial-primary"
                />
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => setShowAddModal(false)}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Abbrechen
              </button>
              <button
                onClick={handleAddInstitution}
                disabled={!newInstitution.name || !newInstitution.city}
                className="px-4 py-2 bg-memorial-primary text-white rounded-lg hover:bg-memorial-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Hinzufügen
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
